import { Button } from '@/common/ui/button'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import { z } from 'zod'
import { useQueryClient } from '@tanstack/react-query'
import { PlusIcon } from 'lucide-react'
import useMutationCreateDynamicPage from '@/modules/client-setting/api/hooks/useMutationCreateDynamicPage'
import { DynamicPageQueryKeys } from '@/modules/client-setting/keys'
import { RequiredIndicator } from '@/common/ui/form/required-indicator'

const formSchema = z.object({
	name: z.string().min(1, 'กรุณากรอกชื่อหน้า'),
	isActive: z.boolean().default(false),
})

type FormValues = z.infer<typeof formSchema>

interface CreateDynamicPageModalProps {
	trigger?: React.ReactNode
}

export default function CreateDynamicPageModal({
	trigger,
}: CreateDynamicPageModalProps) {
	const [open, setOpen] = useState(false)
	const queryClient = useQueryClient()

	const form = useForm<FormValues>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			name: '',
			isActive: false,
		},
	})

	const { mutate: createDynamicPage, isPending: isCreating } =
		useMutationCreateDynamicPage()

	const onSubmit = (values: FormValues) => {
		try {
			createDynamicPage(
				{
					payload: {
						name: values.name.trim(),
						isActive: false,
					},
				},
				{
					onSuccess: () => {
						toast.success('เพิ่มหน้าเรียบร้อยแล้ว')
						queryClient.invalidateQueries({
							queryKey: [DynamicPageQueryKeys.GetDynamicPage],
						})
						form.reset()
						setOpen(false)
					},
					onError: (error) => {
						showErrorToast(error)
					},
				},
			)
		} catch (error) {
			showErrorToast(error as Error)
		}
	}

	const defaultTrigger = (
		<Button variant="default">
			<PlusIcon className="h-4 w-4" />
			เพิ่มหน้า
		</Button>
	)

	return (
		<>
			<div onClick={() => setOpen(true)}>{trigger || defaultTrigger}</div>

			<Dialog open={open} onOpenChange={setOpen}>
				<DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[600px]">
					<DialogHeader>
						<DialogTitle>เพิ่มหน้า</DialogTitle>
					</DialogHeader>

					<Form {...form}>
						<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
							<FormField
								control={form.control}
								name="name"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											<RequiredIndicator /> ชื่อหน้า
										</FormLabel>
										<FormControl>
											<Input {...field} placeholder="กรอกชื่อหน้า" />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<div className="flex justify-end gap-4 pt-4">
								<Button
									variant="outline"
									onClick={() => setOpen(false)}
									type="button"
								>
									ยกเลิก
								</Button>
								<Button type="submit" variant="default" disabled={isCreating}>
									{isCreating ? 'กำลังบันทึก...' : 'บันทึก'}
								</Button>
							</div>
						</form>
					</Form>
				</DialogContent>
			</Dialog>
		</>
	)
}
